import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCreateUser, useUpdateUser, useCreateApiOnlyUser, useApiKeyScopes } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/useToast';
import { User, Bot, Info } from 'lucide-react';
import type { User as UserType, UserType as UserTypeEnum, CreateAPIKeyResponse } from '@/types';
import { ApiKeyDisplayModal } from './ApiKeyDisplayModal';

interface UserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserType | null;
}

export const UserModal: React.FC<UserModalProps> = ({
  open,
  onOpenChange,
  user,
}) => {
  const [userType, setUserType] = useState<UserTypeEnum>('human');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  // API-only user fields
  const [serviceDescription, setServiceDescription] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expirationDays, setExpirationDays] = useState<string>('365');

  // API key display
  const [apiKeyData, setApiKeyData] = useState<CreateAPIKeyResponse | null>(null);
  const [apiKeyDisplayOpen, setApiKeyDisplayOpen] = useState(false);

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const createApiOnlyUser = useCreateApiOnlyUser();
  const { data: scopes } = useApiKeyScopes();
  const { toast } = useToast();

  const isEditMode = !!user;

  // Reset the form when the dialog opens/closes or the edited user changes.
  // Adjusted during render (React's documented pattern for resetting state
  // from props: https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // instead of in an effect: `hasSeeded`/`seededUser`/`seededOpen` mirror what
  // the form was last seeded from. A mismatch — including the first render —
  // seeds the form synchronously and updates the mirrors, so React re-renders
  // once more before committing and the branch converges (no further updates).
  const [hasSeeded, setHasSeeded] = useState(false);
  const [seededUser, setSeededUser] = useState(user);
  const [seededOpen, setSeededOpen] = useState(open);
  if (!hasSeeded || user !== seededUser || open !== seededOpen) {
    setHasSeeded(true);
    setSeededUser(user);
    setSeededOpen(open);
    if (user) {
      setUserType(user.user_type || 'human');
      setEmail(user.email || '');
      setFullName(user.full_name || '');
      setIsActive(user.is_active !== undefined ? user.is_active : true);
      setIsVerified(user.is_verified !== undefined ? user.is_verified : false);
      setIsSuperuser(user.is_superuser !== undefined ? user.is_superuser : false);
    } else {
      // Reset form for create mode
      setUserType('human');
      setEmail('');
      setFullName('');
      setIsActive(true);
      setIsVerified(false);
      setIsSuperuser(false);
      setServiceDescription('');
      setSelectedScopes([]);
      setExpirationDays('365');
    }
  }

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditMode && user) {
        // Update user (exclude password as we don't support password updates via this modal)
        await updateUser.mutateAsync({
          userId: user.id,
          data: {
            email,
            full_name: fullName || undefined,
            is_active: isActive,
            is_verified: isVerified,
            is_superuser: isSuperuser,
          },
        });
        toast({
          title: 'Success',
          description: 'User updated successfully',
        });
        onOpenChange(false);
      } else {
        // Create user
        if (userType === 'api_only') {
          // Validate API-only user fields
          if (selectedScopes.length === 0) {
            toast({
              title: 'Validation Error',
              description: 'Please select at least one permission scope',
              variant: 'destructive',
            });
            return;
          }

          if (!fullName.trim()) {
            toast({
              title: 'Validation Error',
              description: 'Service Account Name is required',
              variant: 'destructive',
            });
            return;
          }

          // Create API-only user
          const result = await createApiOnlyUser.mutateAsync({
            email,
            full_name: fullName,
            user_type: 'api_only',
            is_active: isActive,
            is_verified: true, // API-only users don't need email verification
            is_superuser: false, // API-only users can't be superusers
            api_key: {
              name: `${fullName} - Default Key`,
              description: serviceDescription || undefined,
              scopes: selectedScopes,
              expires_in_days: expirationDays === 'never' ? undefined : parseInt(expirationDays),
            },
          });

          // Backend response already matches CreateAPIKeyResponse format
          const apiKeyResponseForModal: CreateAPIKeyResponse = result;

          // Show API key display modal
          setApiKeyData(apiKeyResponseForModal);
          setApiKeyDisplayOpen(true);
          onOpenChange(false);

          toast({
            title: 'Success',
            description: 'API-only user created successfully',
          });
        } else {
          // Create human user (magic-link flow: no password required, #352)
          await createUser.mutateAsync({
            email,
            full_name: fullName || undefined,
            user_type: 'human',
            is_active: isActive,
            is_verified: isVerified,
            is_superuser: isSuperuser,
          });

          toast({
            title: 'Success',
            description: 'User created successfully',
          });
          onOpenChange(false);
        }
      }
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} user`,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit} autoComplete="off" data-1p-ignore>
            <DialogHeader>
              <DialogTitle>
                {isEditMode
                  ? `Edit User${user?.user_type === 'api_only' ? ' (API-Only)' : ''}`
                  : 'Create User'}
              </DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? 'Update user information and permissions'
                  : 'Choose user type and configure account settings'}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* User Type Selector (Create mode only) */}
              {!isEditMode && (
                <div className="space-y-3">
                  <Label>User Type *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setUserType('human')}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                        userType === 'human'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <User className={`h-6 w-6 ${userType === 'human' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="text-center">
                        <div className="font-medium">Human User</div>
                        <div className="text-xs text-muted-foreground">Username/Password</div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setUserType('api_only')}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                        userType === 'api_only'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Bot className={`h-6 w-6 ${userType === 'api_only' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="text-center">
                        <div className="font-medium">API-Only</div>
                        <div className="text-xs text-muted-foreground">Service Account</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Common Fields */}
              <div className="grid gap-2">
                <Label htmlFor="email">
                  {userType === 'api_only' ? 'Service Account Email' : 'Email'} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={userType === 'api_only' ? 'service-name@example.com' : 'user@example.com'}
                  required
                />
                {userType === 'api_only' && (
                  <p className="text-xs text-muted-foreground">
                    Used for audit logs and notifications
                  </p>
                )}
              </div>

              {/* Human User: password is set by the user via an emailed magic link (#352) */}
              {userType === 'human' && !isEditMode && (
                <div className="rounded-md border border-border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">
                    The user will receive an email with a secure link to set their own password.
                  </p>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="fullName">
                  {userType === 'api_only' ? 'Service Account Name' : 'Full Name'}
                  {userType === 'api_only' && ' *'}
                </Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={userType === 'api_only' ? 'Data Pipeline Service' : 'John Doe'}
                  required={userType === 'api_only'}
                />
                {userType === 'api_only' && (
                  <p className="text-xs text-muted-foreground">
                    Human-readable name for this service account
                  </p>
                )}
              </div>

              {/* API-Only User Fields */}
              {userType === 'api_only' && !isEditMode && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={serviceDescription}
                      onChange={(e) => setServiceDescription(e.target.value)}
                      placeholder="Purpose and usage of this service account"
                    />
                    <p className="text-xs text-muted-foreground">
                      Purpose and usage of this API key
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>API Key Scopes (Permissions) *</Label>
                    <div className="grid grid-cols-2 gap-2 border rounded-md p-3">
                      {scopes && scopes.length > 0 ? (
                        scopes.map((scope) => (
                          <div key={scope.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`scope-${scope.id}`}
                              checked={selectedScopes.includes(scope.name)}
                              onCheckedChange={() => toggleScope(scope.name)}
                            />
                            <label
                              htmlFor={`scope-${scope.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {scope.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <>
                          {['datasets:read', 'datasets:write', 'ingestion:execute', 'views:read'].map(
                            (scope) => (
                              <div key={scope} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`scope-${scope}`}
                                  checked={selectedScopes.includes(scope)}
                                  onCheckedChange={() => toggleScope(scope)}
                                />
                                <label
                                  htmlFor={`scope-${scope}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {scope}
                                </label>
                              </div>
                            )
                          )}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Select permissions this API key will have
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>API Key Expiration</Label>
                    <RadioGroup value={expirationDays} onValueChange={setExpirationDays}>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="30" id="exp-30" />
                          <Label htmlFor="exp-30" className="font-normal">30 days</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="90" id="exp-90" />
                          <Label htmlFor="exp-90" className="font-normal">90 days</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="365" id="exp-365" />
                          <Label htmlFor="exp-365" className="font-normal">1 year</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="never" id="exp-never" />
                          <Label htmlFor="exp-never" className="font-normal">Never</Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <Alert variant="info">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      API-only users cannot log in via the UI. They authenticate using API keys only.
                    </AlertDescription>
                  </Alert>
                </>
              )}

              {/* Human User Toggles */}
              {userType === 'human' && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="isActive">Active</Label>
                      <p className="text-xs text-muted-foreground">User can log in</p>
                    </div>
                    <Switch
                      id="isActive"
                      checked={isActive}
                      onCheckedChange={setIsActive}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="isVerified">Verified</Label>
                      <p className="text-xs text-muted-foreground">Email verified</p>
                    </div>
                    <Switch
                      id="isVerified"
                      checked={isVerified}
                      onCheckedChange={setIsVerified}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="isSuperuser">Superuser</Label>
                      <p className="text-xs text-muted-foreground">Bypass all RBAC checks</p>
                    </div>
                    <Switch
                      id="isSuperuser"
                      checked={isSuperuser}
                      onCheckedChange={setIsSuperuser}
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createUser.isPending || updateUser.isPending || createApiOnlyUser.isPending}
              >
                {createUser.isPending || updateUser.isPending || createApiOnlyUser.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update User'
                  : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Key Display Modal */}
      <ApiKeyDisplayModal
        open={apiKeyDisplayOpen}
        onOpenChange={setApiKeyDisplayOpen}
        apiKeyData={apiKeyData}
        userEmail={email}
        userName={fullName}
      />
    </>
  );
};
